import React, { useEffect, useRef } from 'react';
import { useCloseOnEsc } from '@srcDS/hooks/useCloseOnEsc';
import { useLayoutContext } from '@srcDS/provider/layout/LayoutContext';
import { shallowEqual, useSelector } from 'react-redux';
import { useScrollLock } from '@srcDS/utils/useLockScroll';
import { iconSizeProps, theme } from './_vars';

import Button from '../../atoms/button';
import CloseCross from '../../atoms/icons/CloseCross';
import styles from './SearchPopup.module.scss';
import { useOutsideClick } from '@srcDS/hooks/useOutsideClick';
import { useMenuContext } from '@srcDS/provider/menu/MenuContext';
import { useNostoPlacementProducts } from './hooks/useNostoPlacementProducts';

import { useSearchProductsApi } from '@srcDS/components/organisms/searchDropdown/hooks/useSearchProductsApi';
import { Spinner } from '@srcDS/components/atoms/icons';
import { Text } from '@srcDS/components/typography/text/Text';
import { useNostoContext } from '@srcDS/provider/nosto/NostoContext';
import ProductNostoTile from '@srcDS/components/organisms/searchDropdown/ProductNostoTile';
import ProductResultsTile from '@srcDS/components/organisms/searchDropdown/ProductResultsTile';

const SearchDropdown = ({ staticTrendingQueries, currency, placement, isSSR, noResultsTitle, noResultsSubtitle }) => {
  const { loggedIn, customerGroupId, triggerRoute } = useSelector(
    (state) => ({
      loggedIn: state.app.context.session?.loggedIn,
      customerGroupId: state.app.context.session?.account?.projectSpecificData?.customerGroupId ?? null,
      triggerRoute: state['demo-common'].triggerRoute,
    }),
    shallowEqual,
  );

  const toggleSearchDropdown = (state) => {
    if (window) {
      window.localStorage.removeItem('searchedValue');
    }
    setShowSearchDropdown(state);
  };
  const closeAction = () => toggleSearchDropdown(false);
  const { nostoSearchOnChange, nostoRecordSearchClick, nostoRecordSearchSubmit, nostoRecordSearchAddToCart } =
    useNostoContext();

  const {
    showSearchDropdown,
    setShowSearchDropdown,
    searchValue,
    onChangeSearch,
    gotToSearchPage,
    isTablet,
    isDesktop,
    getPositionToTop,
    searchResults,
    setSearchResults,
    isLoadingSearch,
    isLoadedSearch,
  } = useLayoutContext({ closeAction });

  const { products: recommendedProducts } = useNostoPlacementProducts({
    placement,
    currency,
    isSSR,
    searchPhrase: searchValue,
  });
  const { getSearchResults } = useSearchProductsApi();

  const onSelectQuery = async (value) => {
    onChangeSearch(value);
    const data = await getSearchResults(value);

    nostoSearchOnChange({
      type: 'autocomplete',
      searchRequest: { query: value },
      searchResults: data,
      options: {
        isKeyword: true,
      },
    });
    setSearchResults(data);
  };

  const { isTouch } = useMenuContext();

  const searchPopupRef = useRef(null);

  const DEFAULT_PRODUCTS_COUNT = isDesktop ? 3 : isTablet ? 4 : 2;
  const SEARCHED_PRODUCTS_COUNT = isDesktop ? 6 : 4;

  const suggestionKeywords = searchResults?.suggestionKeywords ?? [];
  const products = searchResults?.products ?? [];

  const hasRecommendedProducts = recommendedProducts?.length > 0;
  const hasStaticTrendingQueries = staticTrendingQueries?.length > 0;
  const hasProducts = products?.length > 0;
  const hasSuggestionKeywords = suggestionKeywords?.length > 0;

  const isNoResults = searchValue && !isLoadingSearch && isLoadedSearch && !hasProducts;

  const productsCount = searchResults?.total;
  const showViewAllButton = hasProducts && productsCount > SEARCHED_PRODUCTS_COUNT;

  const positionToTop = getPositionToTop();
  const inlineStyles = {
    '--positionToTop': `${positionToTop}px`,
  };

  useCloseOnEsc({ closeAction, activate: isDesktop });

  useOutsideClick({
    ref: searchPopupRef,
    handler: (event) => {
      event?.stopPropagation();
      closeAction();
    },
    activate: isDesktop,
    ignoreId: ['header-search-input', 'add-to-cart-popup', 'mobile-minicart-confirmation'],
  });

  useScrollLock(showSearchDropdown);

  useEffect(() => {
    triggerRoute && closeAction();
  }, [triggerRoute]);

  const renderProducts = () => {
    let items = null;

    const productProps = {
      loggedIn,
      customerGroupId,
      nostoRecordSearchClick,
    };

    if (searchValue && hasProducts && isLoadedSearch) {
      items = products.slice(0, SEARCHED_PRODUCTS_COUNT);
      return items.map((product) => (
        <ProductResultsTile product={product} key={`search-product-${product?.productId}`} {...productProps} />
      ));
    } else if (hasRecommendedProducts) {
      items = recommendedProducts.slice(0, DEFAULT_PRODUCTS_COUNT);
      return items.map((product) => (
        <ProductNostoTile product={product} key={`recommended-product-${product?.productId}`} {...productProps} />
      ));
    } else {
      return null;
    }
  };

  const renderQueries = () => {
    let searchQueries = [];

    if (searchValue && hasSuggestionKeywords && isLoadedSearch) {
      searchQueries = suggestionKeywords.map((query) => ({
        label: query?._highlight?.keyword ?? query?.keyword,
        value: query?.keyword,
      }));
    } else {
      searchQueries = staticTrendingQueries.map((query) => ({
        label: query.label,
        value: query.label,
      }));
    }
    if (!searchQueries) return null;

    return searchQueries.map((item, index) => (
      <li
        key={`trending-queries-${index}`}
        onClick={() => onSelectQuery(item.value ?? item.label)}
        dangerouslySetInnerHTML={{ __html: item.label }}
      ></li>
    ));
  };

  return (
    <div className={styles.searchContainer} ref={searchPopupRef}>
      {showSearchDropdown && (
        <>
          <span className={styles.overlay} style={inlineStyles} onClick={closeAction} />
          <div className={styles.searchWrapperOuter} style={{ top: positionToTop, ...inlineStyles }}>
            <div className={styles.searchHeader}>
              <Button variant="primary" as="text" onClick={closeAction}>
                <CloseCross {...iconSizeProps} />
              </Button>
            </div>

            <div className={styles.searchWrapper}>
              <div className={styles.searchContent}>
                {searchValue && isLoadingSearch && (
                  <div className={styles.loader}>
                    <Spinner />
                    <Text as="p">Loading results for ‘{searchValue}’</Text>
                  </div>
                )}
                {isNoResults && (
                  <div className={styles.noResults}>
                    {noResultsTitle && (
                      <Text as="h3">
                        {noResultsTitle} ‘{searchValue}’
                      </Text>
                    )}
                    {noResultsSubtitle && <Text as="p">{noResultsSubtitle}</Text>}
                  </div>
                )}
                {!isNoResults && <h3 className={theme === 'Jag' ? styles.searchContentTitle : ''}>
                  {searchValue && isLoadedSearch ? 'PRODUCTS' : 'You might like these'}
                </h3>}
                <div className={styles.searchContentList}>{renderProducts()}</div>
                {searchValue && showViewAllButton && productsCount && (
                  <div className={styles.viewAllButtonContainer}>
                    <Button
                      variant="primaryOutlined"
                      onClick={() => {
                        nostoRecordSearchSubmit(searchValue);

                        gotToSearchPage(searchValue);
                      }}
                    >
                      View all {productsCount} results
                    </Button>
                  </div>
                )}
              </div>
              <hr />
              <div className={styles.searchSuggestion}>
                <h3>TRENDING</h3>
                {(hasStaticTrendingQueries || hasSuggestionKeywords) && (
                  <ul className={styles.searchSuggestionList}>{renderQueries()}</ul>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
export default SearchDropdown;
